'use client'

/**
 * PriorityBadge Component
 *
 * Visual indicators to help users identify where to start and understand
 * action sequences. Small dots only, no colored backgrounds, so sequence
 * cues stay quiet next to status color.
 *
 * Usage:
 *   <PriorityBadge type="start-here" />
 *   <PriorityBadge type="numbered" priority={1} />
 */

import * as React from 'react'
import { cn } from '@/lib/utils'
import { PriorityConfig } from './types'

interface PriorityBadgeProps extends PriorityConfig {
  /** Additional CSS classes */
  className?: string
  /** Size variant */
  size?: 'sm' | 'md'
}

/**
 * Priority badge for indicating action importance.
 * "Start Here" variant uses a pulsing green dot.
 * Numbered variants show sequence (1, 2, 3).
 */
export function PriorityBadge({
  type,
  priority,
  label,
  className,
  size = 'md',
}: PriorityBadgeProps) {
  const sizeStyles = {
    sm: {
      dot: 'h-2 w-2',
      number: 'h-5 w-5 text-xs',
      text: 'text-xs',
      gap: 'gap-1.5',
    },
    md: {
      dot: 'h-2.5 w-2.5',
      number: 'h-6 w-6 text-sm',
      text: 'text-sm',
      gap: 'gap-2',
    },
  }

  const styles = sizeStyles[size]

  if (type === 'start-here') {
    return (
      <div
        className={cn(
          'inline-flex items-center',
          styles.gap,
          className
        )}
      >
        {/* Pulsing green dot */}
        <span className="relative flex">
          <span
            className={cn(
              'animate-ping absolute inline-flex rounded-full bg-green-400 opacity-75',
              styles.dot
            )}
          />
          <span
            className={cn(
              'relative inline-flex rounded-full bg-green-500',
              styles.dot
            )}
          />
        </span>
        <span className={cn('font-medium text-green-700', styles.text)}>
          {label || 'Start Here'}
        </span>
      </div>
    )
  }

  // Numbered badge
  return (
    <div
      className={cn(
        'inline-flex items-center',
        styles.gap,
        className
      )}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'bg-gray-100 text-gray-700 font-semibold',
          'border border-gray-200',
          styles.number
        )}
      >
        {priority}
      </span>
      {label && (
        <span className={cn('text-gray-700', styles.text)}>
          {label}
        </span>
      )}
    </div>
  )
}

/**
 * Inline priority indicator - just the badge without wrapper
 */
export function PriorityDot({
  type,
  className,
}: {
  type: 'high' | 'medium' | 'low' | 'start'
  className?: string
}) {
  const colors = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-gray-400',
    start: 'bg-green-500',
  }

  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full flex-shrink-0',
        colors[type],
        type === 'start' && 'animate-pulse',
        className
      )}
      aria-hidden="true"
    />
  )
}

/**
 * Action step component combining number badge with content
 */
interface ActionStepProps {
  /** Step number (1, 2, 3, etc.) */
  step: number
  /** Step title */
  title: string
  /** Step description */
  description?: string
  /** Whether this is the current/active step */
  isActive?: boolean
  /** Whether this step is completed */
  isCompleted?: boolean
  /** Additional CSS classes */
  className?: string
  /** Children content */
  children?: React.ReactNode
}

export function ActionStep({
  step,
  title,
  description,
  isActive = false,
  isCompleted = false,
  className,
  children,
}: ActionStepProps) {
  return (
    <div
      className={cn(
        'flex gap-4 p-4 rounded-lg',
        isActive && 'bg-green-50 border border-green-200',
        isCompleted && 'opacity-60',
        !isActive && !isCompleted && 'bg-gray-50',
        className
      )}
    >
      {/* Step number */}
      <div className="flex-shrink-0">
        <span
          className={cn(
            'inline-flex items-center justify-center',
            'h-8 w-8 rounded-full text-sm font-semibold',
            isActive && 'bg-green-500 text-white',
            isCompleted && 'bg-gray-300 text-gray-600',
            !isActive && !isCompleted && 'bg-gray-200 text-gray-700'
          )}
        >
          {isCompleted ? '✓' : step}
        </span>
      </div>

      {/* Step content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4
            className={cn(
              'font-semibold',
              isActive && 'text-green-800',
              isCompleted && 'text-gray-500',
              !isActive && !isCompleted && 'text-gray-800'
            )}
          >
            {title}
          </h4>
          {isActive && (
            <PriorityDot type="start" />
          )}
        </div>
        {description && (
          <p className="text-sm text-gray-600 mt-1">
            {description}
          </p>
        )}
        {children && (
          <div className="mt-2">
            {children}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * List of action steps with connecting line
 */
interface ActionStepListProps {
  steps: Array<{
    title: string
    description?: string
    isActive?: boolean
    isCompleted?: boolean
  }>
  className?: string
}

export function ActionStepList({ steps, className }: ActionStepListProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {steps.map((step, index) => (
        <ActionStep
          key={index}
          step={index + 1}
          title={step.title}
          description={step.description}
          isActive={step.isActive}
          isCompleted={step.isCompleted}
        />
      ))}
    </div>
  )
}

export default PriorityBadge
